import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaSave,
  FaSpinner,
  FaFileInvoice,
  FaBuilding,
  FaMoneyBillWave,
  FaReceipt,
  FaInfoCircle,
  FaArrowLeft,
  FaMapMarkerAlt,
  FaCalendar,
  FaUser,
  FaPhone,
} from 'react-icons/fa';
import { BillingStatus, WccStatus, InvoiceStatus } from './index';
import axiosInstance from '../../libs/axios';

interface BillingFormData {
  project_site_id: number;
  tc_po_number?: string;
  tc_po_date?: string;
  is_po_given_at_allocation: boolean;
  billing_status: BillingStatus;
  activity_amount?: number;
  billed_site_id?: string;
  billed_ref_id?: string;
  billed_po_number?: string;
  billed_po_date?: string;
  wcc_date?: string;
  wcc_number?: string;
  wcc_status: WccStatus;
  receipt_number?: string;
  invoice_number?: string;
  invoice_date?: string;
  invoice_amount?: number;
  invoice_status: InvoiceStatus;
}

interface SiteDetails {
  id: number;
  site_id: string;
  site_name: string;
  site_address: string;
  reference_number: string;
  client_allocation_date: string;
  po_number: string;
  po_date: string;
  pincode: number;
  site_latitude: string;
  site_longitude: string;
  contact_person: string;
  contact_number: string;
  technician_name: string;
  technician_number: string;
  state_circle: {
    circle_name: string;
    state_name: string;
  };
  district: {
    name: string;
  };
  project_id: number;
  activity_id: number;
}

export const AddBillingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [siteDetails, setSiteDetails] = useState<SiteDetails | null>(null);
  const [loadingSite, setLoadingSite] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<BillingFormData>({
    project_site_id: parseInt(id || '0'),
    tc_po_number: '',
    tc_po_date: new Date().toISOString().split('T')[0],
    is_po_given_at_allocation: false,
    billing_status: BillingStatus.PENDING,
    activity_amount: 0,
    billed_site_id: '',
    billed_ref_id: '',
    billed_po_number: '',
    billed_po_date: new Date().toISOString().split('T')[0],
    wcc_date: new Date().toISOString().split('T')[0],
    wcc_number: '',
    wcc_status: WccStatus.PENDING,
    receipt_number: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    invoice_amount: 0,
    invoice_status: InvoiceStatus.DRAFT,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof BillingFormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof BillingFormData, boolean>>>({});

  // Fetch site details on component mount
  useEffect(() => {
    if (id) {
      fetchSiteDetails(parseInt(id));
    }
  }, [id]);

  const fetchSiteDetails = async (id: number) => {
    setLoadingSite(true);
    try {
      const response = await axiosInstance.get(`/survey/sites/${id}`);

      if (response.data.success) {
        const site = response.data.data.site;
        setSiteDetails(site);

        // Auto-populate form fields from site data
        setFormData(prev => ({
          ...prev,
          project_site_id: site.id,
          billed_site_id: site.site_id,
          billed_ref_id: site.reference_number,
          billed_po_number: site.po_number || '',
          billed_po_date: site.po_date ? new Date(site.po_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        }));
      } else {
        navigate(-1);
      }
    } catch (error: any) {
      console.error('Error fetching site details:', error);
      navigate(-1);
    } finally {
      setLoadingSite(false);
    }
  };

  // Validation
  const validateField = (name: keyof BillingFormData, value: any): string => {
    switch (name) {
      case 'activity_amount':
        if (value && value < 0) return 'Activity amount cannot be negative';
        return '';
      case 'invoice_amount':
        if (value && value < 0) return 'Invoice amount cannot be negative';
        return '';
      case 'billed_ref_id':
        if (value && value.trim().length > 100) return 'Reference ID cannot exceed 100 characters';
        return '';
      case 'billed_site_id':
        if (value && value.trim().length > 50) return 'Site ID cannot exceed 50 characters';
        return '';
      case 'tc_po_number':
      case 'billed_po_number':
      case 'wcc_number':
      case 'receipt_number':
      case 'invoice_number':
        if (value && value.trim().length > 100) return 'Field cannot exceed 100 characters';
        return '';
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof BillingFormData;
      const error = validateField(fieldKey, formData[fieldKey]);
      if (error) {
        newErrors[fieldKey] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const fieldName = name as keyof BillingFormData;

    let finalValue: any = value;

    if (type === 'number') {
      finalValue = value === '' ? undefined : parseFloat(value);
    } else if (type === 'checkbox') {
      finalValue = (e.target as HTMLInputElement).checked;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldName]: finalValue,
    }));

    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: '',
      }));
    }

    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof BillingFormData;

    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    const error = validateField(fieldName, value);
    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const allTouched: any = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key as keyof BillingFormData] = true;
    });
    setTouched(allTouched);

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const cleanedData = Object.fromEntries(
          Object.entries(formData).filter(([_, v]) => v !== undefined && v !== '')
        ) as BillingFormData;

        await axiosInstance.post('/billing', cleanedData);
        navigate(-1);
      } catch (error: any) {
        console.error('Error creating billing:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (loadingSite) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading site details...</p>
        </div>
      </div>
    );
  }

  if (!siteDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Site not found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 mb-4">
        <div className="mx-auto p-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              {/* <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                disabled={isSubmitting}
              >
                <FaArrowLeft className="w-5 h-5 text-gray-600" />
              </button> */}
              <div className="flex items-center gap-3">
                {/* <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaFileInvoice className="w-5 h-5 text-blue-600" />
                </div> */}
                <div>
                  <h1 className="text-xl font-semibold text-gray-800">Add New Billing</h1>
                  <p className="text-sm text-gray-600">Create billing for site: {siteDetails.site_id}</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  Create Billing
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Site Details Card (Sticky) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 top-24">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FaBuilding className="w-5 h-5 text-blue-600" />
                  Site Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {/* Site ID & Name */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Site ID</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{siteDetails.site_id}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Site Name</p>
                  <p className="mt-1 text-sm text-gray-900">{siteDetails.site_name}</p>
                </div>

                {/* Reference Number */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Reference Number</p>
                  <p className="mt-1 text-sm text-gray-900">{siteDetails.reference_number}</p>
                </div>

                {/* Location */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                    <FaMapMarkerAlt className="w-3 h-3" />
                    Location
                  </p>
                  <p className="mt-1 text-sm text-gray-900">{siteDetails.site_address}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {siteDetails.district?.name}, {siteDetails.state_circle?.state_name} - {siteDetails.pincode}
                  </p>
                </div>

                {/* Allocation Date */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                    <FaCalendar className="w-3 h-3" />
                    Allocation Date
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(siteDetails.client_allocation_date).toLocaleDateString()}
                  </p>
                </div>

                {/* PO Details */}
                {siteDetails.po_number && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">PO Number</p>
                    <p className="mt-1 text-sm text-gray-900">{siteDetails.po_number}</p>
                    {siteDetails.po_date && (
                      <p className="text-xs text-gray-600 mt-1">
                        Date: {new Date(siteDetails.po_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Contact Person */}
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                    <FaUser className="w-3 h-3" />
                    Contact Person
                  </p>
                  <p className="mt-1 text-sm text-gray-900">{siteDetails.contact_person}</p>
                  <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                    <FaPhone className="w-3 h-3" />
                    {siteDetails.contact_number}
                  </p>
                </div>

                {/* Technician */}
                {siteDetails.technician_name && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Technician</p>
                    <p className="mt-1 text-sm text-gray-900">{siteDetails.technician_name}</p>
                    <p className="text-xs text-gray-600 mt-1">{siteDetails.technician_number}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Billing Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* TC PO Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FaReceipt className="w-4 h-4" />
                  TC Purchase Order Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="is_po_given_at_allocation"
                        checked={formData.is_po_given_at_allocation}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        PO Number Given at Time of Allocation
                      </span>
                    </label>
                    <p className="mt-1 text-xs text-gray-500 ml-6">
                      TC can update PO number later if not available now
                    </p>
                  </div>

                  <div>
                    <label htmlFor="tc_po_number" className="block text-sm font-medium text-gray-700 mb-2">
                      TC PO Number
                    </label>
                    <input
                      type="text"
                      id="tc_po_number"
                      name="tc_po_number"
                      value={formData.tc_po_number || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.tc_po_number && touched.tc_po_number
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                      placeholder="Enter TC PO number"
                      maxLength={100}
                    />
                    {errors.tc_po_number && touched.tc_po_number && (
                      <p className="mt-1 text-sm text-red-600">{errors.tc_po_number}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="tc_po_date" className="block text-sm font-medium text-gray-700 mb-2">
                      TC PO Date
                    </label>
                    <input
                      type="date"
                      id="tc_po_date"
                      name="tc_po_date"
                      value={formData.tc_po_date || ''}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>

              {/* Basic Billing Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Billing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="billing_status" className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Status
                    </label>
                    <select
                      id="billing_status"
                      name="billing_status"
                      value={formData.billing_status}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <option value={BillingStatus.PENDING}>Pending</option>
                      <option value={BillingStatus.WCC_RAISED}>WCC Raised</option>
                      <option value={BillingStatus.WCC_APPROVED}>WCC Approved</option>
                      <option value={BillingStatus.INVOICE_RAISED}>Invoice Raised</option>
                      <option value={BillingStatus.INVOICE_SUBMITTED}>Invoice Submitted</option>
                      <option value={BillingStatus.PAID}>Paid</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Status will auto-update based on WCC/invoice actions
                    </p>
                  </div>

                  <div>
                    <label htmlFor="activity_amount" className="block text-sm font-medium text-gray-700 mb-2">
                      Activity Amount (₹)
                    </label>
                    <input
                      type="number"
                      id="activity_amount"
                      name="activity_amount"
                      value={formData.activity_amount || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.activity_amount && touched.activity_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                      placeholder="Enter activity amount"
                    />
                    {errors.activity_amount && touched.activity_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.activity_amount}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Billed Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">
                  Billed Details
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    (Auto-populated from site data, editable)
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="billed_site_id" className="block text-sm font-medium text-gray-700 mb-2">
                      Billed Site ID
                    </label>
                    <input
                      type="text"
                      id="billed_site_id"
                      name="billed_site_id"
                      value={formData.billed_site_id || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Auto-populated or enter manually"
                      maxLength={50}
                    />
                  </div>

                  <div>
                    <label htmlFor="billed_ref_id" className="block text-sm font-medium text-gray-700 mb-2">
                      Billed Reference ID
                    </label>
                    <input
                      type="text"
                      id="billed_ref_id"
                      name="billed_ref_id"
                      value={formData.billed_ref_id || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Auto-populated or enter manually"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label htmlFor="billed_po_number" className="block text-sm font-medium text-gray-700 mb-2">
                      Billed PO Number
                    </label>
                    <input
                      type="text"
                      id="billed_po_number"
                      name="billed_po_number"
                      value={formData.billed_po_number || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Auto-populated or enter manually"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label htmlFor="billed_po_date" className="block text-sm font-medium text-gray-700 mb-2">
                      Billed PO Date
                    </label>
                    <input
                      type="date"
                      id="billed_po_date"
                      name="billed_po_date"
                      value={formData.billed_po_date || ''}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>

              {/* WCC Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">WCC Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="wcc_number" className="block text-sm font-medium text-gray-700 mb-2">
                      WCC Number
                    </label>
                    <input
                      type="text"
                      id="wcc_number"
                      name="wcc_number"
                      value={formData.wcc_number || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Enter WCC number"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label htmlFor="wcc_date" className="block text-sm font-medium text-gray-700 mb-2">
                      WCC Date
                    </label>
                    <input
                      type="date"
                      id="wcc_date"
                      name="wcc_date"
                      value={formData.wcc_date || ''}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label htmlFor="wcc_status" className="block text-sm font-medium text-gray-700 mb-2">
                      WCC Status
                    </label>
                    <select
                      id="wcc_status"
                      name="wcc_status"
                      value={formData.wcc_status}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <option value={WccStatus.PENDING}>Pending</option>
                      <option value={WccStatus.APPROVED}>Approved</option>
                      <option value={WccStatus.REJECTED}>Rejected</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="receipt_number" className="block text-sm font-medium text-gray-700 mb-2">
                      Receipt Number
                    </label>
                    <input
                      type="text"
                      id="receipt_number"
                      name="receipt_number"
                      value={formData.receipt_number || ''}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Enter receipt number"
                      maxLength={100}
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FaMoneyBillWave className="w-4 h-4" />
                  Invoice Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      id="invoice_number"
                      name="invoice_number"
                      value={formData.invoice_number || ''}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Enter invoice number"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label htmlFor="invoice_date" className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Date
                    </label>
                    <input
                      type="date"
                      id="invoice_date"
                      name="invoice_date"
                      value={formData.invoice_date || ''}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label htmlFor="invoice_amount" className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Amount (₹)
                    </label>
                    <input
                      type="number"
                      id="invoice_amount"
                      name="invoice_amount"
                      value={formData.invoice_amount || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.invoice_amount && touched.invoice_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                      placeholder="Enter invoice amount"
                    />
                    {errors.invoice_amount && touched.invoice_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.invoice_amount}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="invoice_status" className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Status
                    </label>
                    <select
                      id="invoice_status"
                      name="invoice_status"
                      value={formData.invoice_status}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <option value={InvoiceStatus.DRAFT}>Draft</option>
                      <option value={InvoiceStatus.SUBMITTED}>Submitted</option>
                      <option value={InvoiceStatus.APPROVED}>Approved</option>
                      <option value={InvoiceStatus.PAID}>Paid</option>
                      <option value={InvoiceStatus.CANCELLED}>Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Create Billing
                  </>
                )}
              </button>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FaInfoCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Billing Guidelines</p>
                    <ul className="text-xs space-y-1 list-disc list-inside">
                      <li>Site details are auto-populated from the selected site</li>
                      <li>Billed details can be edited if needed</li>
                      <li>TC can update PO number later if not available now</li>
                      <li>Billing status will auto-update based on WCC/invoice actions</li>
                      <li>All optional fields can be filled in later using update endpoints</li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};